import { useState, useMemo, useRef } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import {
  calculatePaycheck,
  PAY_FREQUENCIES,
  STATE_TAX_DATA,
  getBracketBreakdown,
  calculateMarginalRate,
  STANDARD_DEDUCTIONS,
  FICA_CONSTANTS,
} from '../../lib/calculations'
import GlassCard from '../ui/GlassCard'
import StatCard from '../ui/StatCard'
import Button from '../ui/Button'
import { trackExportReport } from '../../lib/analytics'
import { addChartToPDF } from '../../lib/pdfChartCapture'

const FILING_STATUSES = [
  { key: 'single', label: 'Single' },
  { key: 'mfj', label: 'Married Filing Jointly' },
  { key: 'hoh', label: 'Head of Household' },
]

export default function PaycheckCalculator() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  // Input state
  const [payFrequency, setPayFrequency] = useState('biweekly')
  const [grossPay, setGrossPay] = useState(3000)
  const [filingStatus, setFilingStatus] = useState('single')
  const [stateCode, setStateCode] = useState('OH')
  const [customStateTaxRate, setCustomStateTaxRate] = useState(0)

  // Pre-tax deductions
  const [use401kPercent, setUse401kPercent] = useState(true)
  const [traditional401kPercent, setTraditional401kPercent] = useState(6)
  const [traditional401kDollar, setTraditional401kDollar] = useState(0)
  const [hsaPerPeriod, setHsaPerPeriod] = useState(0)
  const [healthInsurance, setHealthInsurance] = useState(0)

  // Post-tax deductions
  const [useRoth401kPercent, setUseRoth401kPercent] = useState(true)
  const [roth401kPercent, setRoth401kPercent] = useState(0)
  const [roth401kDollar, setRoth401kDollar] = useState(0)
  const [otherPostTax, setOtherPostTax] = useState(0)
  const [loanRepayment401k, setLoanRepayment401k] = useState(0)
  const [unionDues, setUnionDues] = useState(0)
  const [lifeDisabilityInsurance, setLifeDisabilityInsurance] = useState(0)
  const [otherMiscPostTax, setOtherMiscPostTax] = useState(0)

  // Pay stub actuals
  const [showStubActuals, setShowStubActuals] = useState(false)
  const [actualFederalWithheld, setActualFederalWithheld] = useState(0)
  const [actualStateWithheld, setActualStateWithheld] = useState(0)
  const [actualSSWithheld, setActualSSWithheld] = useState(0)
  const [actualMedicareWithheld, setActualMedicareWithheld] = useState(0)

  // Collapsible sections
  const [showPayDetails, setShowPayDetails] = useState(true)
  const [showPreTax, setShowPreTax] = useState(true)
  const [showPostTax, setShowPostTax] = useState(true)

  // W-4 adjustments
  const [showW4, setShowW4] = useState(false)
  const [extraWithholding, setExtraWithholding] = useState(0)
  const [dependentsCredit, setDependentsCredit] = useState(0)
  const [otherIncome, setOtherIncome] = useState(0)
  const [deductionsAboveStandard, setDeductionsAboveStandard] = useState(0)

  // Optimizer state
  const [optimizer401k, setOptimizer401k] = useState(null) // null = not active

  const result = useMemo(() => calculatePaycheck({
    grossPay,
    payFrequency,
    filingStatus,
    stateCode,
    customStateTaxRate,
    traditional401kPercent,
    traditional401kDollar,
    use401kPercent,
    hsaPerPeriod,
    healthInsurance,
    roth401kPercent,
    roth401kDollar,
    useRoth401kPercent,
    otherPostTax,
    loanRepayment401k,
    unionDues,
    lifeDisabilityInsurance,
    otherMiscPostTax,
    extraWithholding,
    dependentsCredit,
    otherIncome,
    deductionsAboveStandard,
    actualFederalWithheld: showStubActuals ? actualFederalWithheld : null,
    actualStateWithheld: showStubActuals ? actualStateWithheld : null,
    actualSSWithheld: showStubActuals ? actualSSWithheld : null,
    actualMedicareWithheld: showStubActuals ? actualMedicareWithheld : null,
  }), [grossPay, payFrequency, filingStatus, stateCode, customStateTaxRate,
    traditional401kPercent, traditional401kDollar, use401kPercent, hsaPerPeriod,
    healthInsurance, roth401kPercent, roth401kDollar, useRoth401kPercent,
    otherPostTax, loanRepayment401k, unionDues, lifeDisabilityInsurance, otherMiscPostTax,
    extraWithholding, dependentsCredit, otherIncome, deductionsAboveStandard,
    showStubActuals, actualFederalWithheld, actualStateWithheld, actualSSWithheld, actualMedicareWithheld])

  const freq = PAY_FREQUENCIES[payFrequency]

  // Tax benchmark data
  const benchmark = useMemo(() => {
    const annualGross = result.annualGross
    const deduction = STANDARD_DEDUCTIONS[filingStatus] || STANDARD_DEDUCTIONS.single

    const adjustedAnnualIncome = annualGross - (result.totalPreTax * freq.periods) + otherIncome
    const adjustedDeduction = deduction + deductionsAboveStandard
    const taxableIncome = Math.max(0, adjustedAnnualIncome - adjustedDeduction)
    const brackets = getBracketBreakdown(taxableIncome, filingStatus)
    const marginalRate = calculateMarginalRate(taxableIncome, filingStatus)

    const federalEffective = annualGross > 0
      ? Math.round((result.annualFederalTax / annualGross) * 1000) / 10
      : 0

    const totalAnnualTax = result.annualFederalTax + result.annualStateTax + result.annualSS + result.annualMedicare
    const totalEffective = annualGross > 0
      ? Math.round((totalAnnualTax / annualGross) * 1000) / 10
      : 0

    const ficaRate = annualGross > 0
      ? Math.round(((result.annualSS + result.annualMedicare) / annualGross) * 1000) / 10
      : 0

    const hasActuals = result.actualFederalWithheld !== null
    const withholdingFederal = hasActuals
      ? (result.actualFederalWithheld + extraWithholding) * freq.periods
      : result.federalTax * freq.periods
    const withholdingState = hasActuals ? result.actualStateWithheld * freq.periods : result.stateTax * freq.periods
    const withholdingSS = hasActuals ? result.actualSSWithheld * freq.periods : result.socialSecurity * freq.periods
    const withholdingMedicare = hasActuals ? result.actualMedicareWithheld * freq.periods : result.medicare * freq.periods
    const currentAnnualWithholding = withholdingFederal + withholdingState + withholdingSS + withholdingMedicare

    const withholdingDiff = currentAnnualWithholding - totalAnnualTax

    return {
      brackets, marginalRate, federalEffective, totalEffective, ficaRate,
      taxableIncome, totalAnnualTax, annualGross, currentAnnualWithholding,
      withholdingDiff, hasActuals, withholdingFederal, withholdingState,
      withholdingSS, withholdingMedicare,
    }
  }, [result, filingStatus, freq.periods, otherIncome, deductionsAboveStandard, extraWithholding])

  // Optimizer: calculate impact of different 401k %
  const optimizerResult = useMemo(() => {
    if (optimizer401k === null) return null
    const optimizerCalc = calculatePaycheck({
      grossPay, payFrequency, filingStatus, stateCode, customStateTaxRate,
      traditional401kPercent: optimizer401k,
      traditional401kDollar: 0,
      use401kPercent: true,
      hsaPerPeriod, healthInsurance, roth401kPercent, roth401kDollar, useRoth401kPercent,
      otherPostTax, loanRepayment401k, unionDues, lifeDisabilityInsurance, otherMiscPostTax,
      extraWithholding, dependentsCredit, otherIncome, deductionsAboveStandard,
      actualFederalWithheld: null, actualStateWithheld: null, actualSSWithheld: null, actualMedicareWithheld: null,
    })
    const currentPercent = use401kPercent ? traditional401kPercent : Math.round((traditional401kDollar / grossPay) * 100 * 10) / 10
    const takeHomeDiff = optimizerCalc.netPay - result.netPay
    const annualTaxSavings = result.annualFederalTax - optimizerCalc.annualFederalTax + result.annualStateTax - optimizerCalc.annualStateTax
    const annual401kIncrease = (optimizerCalc.traditional401k - result.traditional401k) * freq.periods
    return {
      currentPercent,
      newPercent: optimizer401k,
      takeHomeDiff,
      annualTaxSavings,
      annual401kIncrease,
      newNetPay: optimizerCalc.netPay,
    }
  }, [optimizer401k, grossPay, payFrequency, filingStatus, stateCode, customStateTaxRate,
    traditional401kPercent, traditional401kDollar, use401kPercent,
    hsaPerPeriod, healthInsurance, roth401kPercent, roth401kDollar, useRoth401kPercent,
    otherPostTax, loanRepayment401k, unionDues, lifeDisabilityInsurance, otherMiscPostTax,
    extraWithholding, dependentsCredit, otherIncome, deductionsAboveStandard, result, freq.periods])

  // HSA max limits (2025)
  const hsaMaxSelf = 4300
  const hsaMaxFamily = 8550
  const currentAnnualHSA = hsaPerPeriod * freq.periods
  const hsaSavingsSelf = currentAnnualHSA < hsaMaxSelf
    ? Math.round((hsaMaxSelf - currentAnnualHSA) * (benchmark.marginalRate / 100))
    : 0
  const hsaSavingsFamily = currentAnnualHSA < hsaMaxFamily
    ? Math.round((hsaMaxFamily - currentAnnualHSA) * (benchmark.marginalRate / 100))
    : 0

  // Pie chart data
  const pieData = [
    { name: 'Take-Home', value: Math.max(0, result.netPay), color: colors.positive },
    { name: 'Federal Tax', value: result.federalTax, color: '#3b82f6' },
    { name: 'State Tax', value: result.stateTax, color: '#8b5cf6' },
    { name: 'Social Security', value: result.socialSecurity, color: '#f59e0b' },
    { name: 'Medicare', value: result.medicare, color: '#ef4444' },
    { name: 'Pre-Tax Deductions', value: result.totalPreTax, color: '#06b6d4' },
    { name: 'Post-Tax Deductions', value: result.totalPostTax, color: '#6366f1' },
  ].filter(d => d.value > 0)

  // Waterfall data
  const waterfall = useMemo(() => {
    const items = []
    let running = result.grossPay
    const addItem = (label, amount, type) => {
      if (amount === 0) return
      running -= amount
      items.push({ label, amount: -amount, running, type })
    }
    items.push({ label: 'Gross Pay', amount: result.grossPay, running, type: 'gross' })
    addItem('401(k)', result.traditional401k, 'deduction')
    addItem('HSA', result.hsa, 'deduction')
    addItem('Health Insurance', result.healthInsurance, 'deduction')
    addItem('Federal Tax', result.federalTax, 'tax')
    if (result.stateTax > 0) addItem('State Tax', result.stateTax, 'tax')
    addItem('Social Security', result.socialSecurity, 'tax')
    addItem('Medicare', result.medicare, 'tax')
    addItem('Roth 401(k)', result.roth401k, 'deduction')
    addItem('Other Post-Tax', result.otherPostTax, 'deduction')
    addItem('401(k) Loan', result.loanRepayment401k, 'deduction')
    addItem('Union Dues', result.unionDues, 'deduction')
    addItem('Life/Disability Ins.', result.lifeDisabilityInsurance, 'deduction')
    addItem('Other Misc.', result.otherMiscPostTax, 'deduction')
    items.push({ label: 'Net Pay', amount: result.netPay, running: result.netPay, type: 'net' })
    return items
  }, [result])

  const handleExportPDF = async () => {
    try {
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14
    const maxContentWidth = pageWidth - margin * 2

    // Shared table styling for clean white background
    const tableStyles = {
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.3 },
      headStyles: { fillColor: [41, 65, 122], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 249, 250] },
    }

    // Helper to ensure there's enough space on the page, otherwise add a new page
    const ensureSpace = (yPos, needed) => {
      if (yPos + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return yPos
    }

    // Helper for section headers
    const addSectionHeader = (title, yPos) => {
      yPos = ensureSpace(yPos, 20)
      doc.setFontSize(13)
      doc.setTextColor(41, 65, 122)
      doc.setFont('helvetica', 'bold')
      doc.text(title, margin, yPos)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(0)
      return yPos + 7
    }

    const filingLabel = filingStatus === 'mfj' ? 'Married Filing Jointly' : filingStatus === 'hoh' ? 'Head of Household' : 'Single'

    // =========================================================================
    // 1. HEADER
    // =========================================================================
    doc.setFontSize(22)
    doc.setTextColor(41, 65, 122)
    doc.setFont('helvetica', 'bold')
    doc.text('Paycheck Breakdown', margin, 22)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(100)
    doc.text(`${freq.label}  |  ${filingLabel}  |  ${stateCode}`, margin, 30)
    doc.text(`Generated by Liquid Finance  --  ${new Date().toLocaleDateString()}`, margin, 36)

    // =========================================================================
    // 2. SUMMARY CARDS
    // =========================================================================
    let y = 44
    const cardData = [
      { label: 'Gross Pay', value: formatCurrency(result.grossPay), sub: freq.label },
      { label: 'Total Taxes', value: formatCurrency(result.totalTax), sub: `${result.effectiveRate}% effective` },
      { label: 'Take-Home', value: formatCurrency(result.netPay), sub: `${formatCurrency(result.annualNet)}/yr` },
      { label: 'Effective Rate', value: `${result.effectiveRate}%`, sub: 'Tax only' },
    ]

    const cardWidth = (maxContentWidth - 9) / 4 // 3 gaps of 3pt between 4 cards
    cardData.forEach((card, i) => {
      const x = margin + i * (cardWidth + 3)
      doc.setFillColor(248, 249, 250)
      doc.roundedRect(x, y, cardWidth, 22, 2, 2, 'F')
      doc.setFontSize(7)
      doc.setTextColor(120)
      doc.text(card.label, x + 4, y + 7)
      doc.setFontSize(12)
      doc.setTextColor(30)
      doc.setFont('helvetica', 'bold')
      doc.text(card.value, x + 4, y + 15)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
      doc.setTextColor(140)
      doc.text(card.sub, x + 4, y + 20)
    })
    y += 30

    // =========================================================================
    // 3. PAY BREAKDOWN WATERFALL
    // =========================================================================
    y = addSectionHeader('Pay Breakdown', y)

    const waterfallRows = waterfall.map(row => {
      if (row.type === 'gross') {
        return [{ content: row.label, styles: { fontStyle: 'bold' } }, '', { content: formatCurrency(row.amount), styles: { fontStyle: 'bold', halign: 'right' } }]
      }
      if (row.type === 'net') {
        return [{ content: row.label, styles: { fontStyle: 'bold' } }, '', { content: formatCurrency(row.amount), styles: { fontStyle: 'bold', halign: 'right' } }]
      }
      return [
        row.label,
        { content: `-${formatCurrency(Math.abs(row.amount))}`, styles: { halign: 'right', textColor: row.type === 'tax' ? [220, 50, 50] : [120, 120, 120] } },
        { content: formatCurrency(row.running), styles: { halign: 'right' } },
      ]
    })

    autoTable(doc, {
      head: [['Item', 'Deduction', 'Running Total']],
      body: waterfallRows,
      startY: y,
      ...tableStyles,
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 45, halign: 'right' }, 2: { cellWidth: 45, halign: 'right' } },
    })
    y = doc.lastAutoTable.finalY + 10

    // =========================================================================
    // 4. PER-PERIOD VS ANNUAL COMPARISON TABLE
    // =========================================================================
    y = addSectionHeader('Per-Period vs Annual', y)

    const comparisonRows = [
      ['Gross Pay', formatCurrency(result.grossPay), formatCurrency(result.annualGross)],
      ...(result.totalPreTax > 0 ? [['Pre-Tax Deductions', formatCurrency(result.totalPreTax), formatCurrency(result.totalPreTax * freq.periods)]] : []),
      ['Federal Tax', formatCurrency(result.federalTax), formatCurrency(result.annualFederalTax)],
      ...(result.stateTax > 0 ? [['State Tax', formatCurrency(result.stateTax), formatCurrency(result.annualStateTax)]] : []),
      ['Social Security', formatCurrency(result.socialSecurity), formatCurrency(result.annualSS)],
      ['Medicare', formatCurrency(result.medicare), formatCurrency(result.annualMedicare)],
      ...(result.roth401k > 0 ? [['Roth 401(k)', formatCurrency(result.roth401k), formatCurrency(result.roth401k * freq.periods)]] : []),
      ...(result.otherPostTax > 0 ? [['Other Post-Tax', formatCurrency(result.otherPostTax), formatCurrency(result.otherPostTax * freq.periods)]] : []),
      ...(result.loanRepayment401k > 0 ? [['401(k) Loan', formatCurrency(result.loanRepayment401k), formatCurrency(result.loanRepayment401k * freq.periods)]] : []),
      ...(result.unionDues > 0 ? [['Union Dues', formatCurrency(result.unionDues), formatCurrency(result.unionDues * freq.periods)]] : []),
      ...(result.lifeDisabilityInsurance > 0 ? [['Life/Disability Ins.', formatCurrency(result.lifeDisabilityInsurance), formatCurrency(result.lifeDisabilityInsurance * freq.periods)]] : []),
      ...(result.otherMiscPostTax > 0 ? [['Other Misc.', formatCurrency(result.otherMiscPostTax), formatCurrency(result.otherMiscPostTax * freq.periods)]] : []),
    ]
    // Net Pay row with bold styling
    const netPayRow = [
      { content: 'Net Pay', styles: { fontStyle: 'bold' } },
      { content: formatCurrency(result.netPay), styles: { fontStyle: 'bold', halign: 'right' } },
      { content: formatCurrency(result.annualNet), styles: { fontStyle: 'bold', halign: 'right' } },
    ]

    autoTable(doc, {
      head: [['Category', freq.label, 'Annual']],
      body: [...comparisonRows, netPayRow],
      startY: y,
      ...tableStyles,
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 50, halign: 'right' }, 2: { cellWidth: 50, halign: 'right' } },
    })
    y = doc.lastAutoTable.finalY + 10

    // =========================================================================
    // 5. PAY DISTRIBUTION PIE CHART
    // =========================================================================
    await addChartToPDF(doc, chartRef, { title: 'Pay Distribution' })
    // addChartToPDF adds its own page, so reset y for next content
    y = margin + 4

    // =========================================================================
    // 6. TAX BENCHMARK
    // =========================================================================
    doc.addPage()
    y = margin + 4
    y = addSectionHeader('Tax Benchmark', y)

    doc.setFontSize(8)
    doc.setTextColor(100)
    doc.text(`Based on ${formatCurrency(benchmark.annualGross)}/yr gross income, ${filingLabel} filing status`, margin, y)
    y += 6

    const benchmarkRows = [
      ['Marginal Tax Rate', `${benchmark.marginalRate}%`, 'Your highest tax bracket'],
      ['Federal Effective Rate', `${benchmark.federalEffective}%`, 'What you actually pay in federal taxes'],
      ['Total Effective Rate', `${benchmark.totalEffective}%`, 'All taxes combined (fed + state + FICA)'],
      ['FICA Rate', `${benchmark.ficaRate}%`, 'Social Security + Medicare'],
      ['Taxable Income', formatCurrency(benchmark.taxableIncome), 'After standard deduction & pre-tax'],
    ]

    autoTable(doc, {
      head: [['Metric', 'Value', 'Description']],
      body: benchmarkRows,
      startY: y,
      ...tableStyles,
      columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 35, halign: 'right' }, 2: { cellWidth: 'auto', textColor: [100, 100, 100], fontStyle: 'italic' } },
    })
    y = doc.lastAutoTable.finalY + 10

    // =========================================================================
    // 7. FEDERAL BRACKET BREAKDOWN
    // =========================================================================
    if (benchmark.brackets.length > 0) {
      y = addSectionHeader('Federal Bracket Breakdown', y)

      const bracketRows = benchmark.brackets.map(b => [
        `${b.rate}%`,
        formatCurrency(b.taxableAmount),
        formatCurrency(b.tax),
        `${Math.round(b.fillPercent)}% filled`,
      ])

      autoTable(doc, {
        head: [['Rate', 'Taxable Amount', 'Tax Owed', 'Bracket Fill']],
        body: bracketRows,
        startY: y,
        ...tableStyles,
        columnStyles: {
          0: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
          1: { cellWidth: 50, halign: 'right' },
          2: { cellWidth: 45, halign: 'right' },
          3: { cellWidth: 40, halign: 'right', textColor: [100, 100, 100] },
        },
      })
      y = doc.lastAutoTable.finalY + 10
    }

    // =========================================================================
    // 8. WITHHOLDING VS ESTIMATED LIABILITY
    // =========================================================================
    y = addSectionHeader('Withholding vs Estimated Liability', y)

    if (benchmark.hasActuals) {
      doc.setFontSize(8)
      doc.setTextColor(59, 130, 246) // blue color for actuals indicator
      doc.text('(using pay stub data)', margin + doc.getTextWidth('Withholding vs Estimated Liability') - 45, y - 5)
      doc.setTextColor(0)
    }

    const withholdingRows = [
      ['Federal Income Tax', formatCurrency(benchmark.withholdingFederal), formatCurrency(result.annualFederalTax)],
      ['State Income Tax', formatCurrency(benchmark.withholdingState), formatCurrency(result.annualStateTax)],
      ['Social Security', formatCurrency(benchmark.withholdingSS), formatCurrency(result.annualSS)],
      ['Medicare', formatCurrency(benchmark.withholdingMedicare), formatCurrency(result.annualMedicare)],
      [
        { content: 'Total', styles: { fontStyle: 'bold' } },
        { content: formatCurrency(benchmark.currentAnnualWithholding), styles: { fontStyle: 'bold', halign: 'right' } },
        { content: formatCurrency(benchmark.totalAnnualTax), styles: { fontStyle: 'bold', halign: 'right' } },
      ],
    ]

    autoTable(doc, {
      head: [['Category', 'Withholding/yr', 'Estimated/yr']],
      body: withholdingRows,
      startY: y,
      ...tableStyles,
      columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 50, halign: 'right' }, 2: { cellWidth: 50, halign: 'right' } },
    })
    y = doc.lastAutoTable.finalY + 4

    // Withholding status message
    let statusText = ''
    let statusDetail = ''
    if (Math.abs(benchmark.withholdingDiff) < benchmark.annualGross * 0.01) {
      statusText = 'On track'
      statusDetail = 'Your withholding closely matches your estimated tax liability.'
    } else if (benchmark.withholdingDiff > 0) {
      statusText = `Over-withheld by ~${formatCurrency(Math.abs(benchmark.withholdingDiff))}`
      statusDetail = 'You may get a refund. Consider adjusting W-4 to keep more per paycheck.'
    } else {
      statusText = `Under-withheld by ~${formatCurrency(Math.abs(benchmark.withholdingDiff))}`
      statusDetail = 'You may owe at tax time. Consider increasing withholding on your W-4.'
    }
    y = ensureSpace(y, 14)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(41, 65, 122)
    doc.text(statusText, margin, y + 4)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(100)
    doc.text(`-- ${statusDetail}`, margin + doc.getTextWidth(statusText + ' '), y + 4)
    doc.setTextColor(0)
    y += 14

    // =========================================================================
    // 9. OPTIMIZER 401(k) WHAT-IF
    // =========================================================================
    if (optimizerResult) {
      y = addSectionHeader('Optimizer: 401(k) What-If', y)

      const currentPctLabel = `${Math.round(optimizerResult.currentPercent)}%`
      const newPctLabel = `${optimizerResult.newPercent}%`

      const optimizerRows = [
        ['Current 401(k) Rate', currentPctLabel, ''],
        ['What-If 401(k) Rate', newPctLabel, ''],
        ['Take-Home Impact', formatCurrency(Math.abs(optimizerResult.takeHomeDiff)) + '/paycheck', optimizerResult.takeHomeDiff < 0 ? 'Decrease' : optimizerResult.takeHomeDiff > 0 ? 'Increase' : 'No change'],
        ['Annual Tax Savings', formatCurrency(Math.abs(optimizerResult.annualTaxSavings)) + '/yr', optimizerResult.annualTaxSavings > 0 ? 'Saved' : optimizerResult.annualTaxSavings < 0 ? 'Additional' : 'No change'],
        ['Retirement Increase', formatCurrency(Math.abs(optimizerResult.annual401kIncrease)) + '/yr', optimizerResult.annual401kIncrease > 0 ? 'More to retirement' : optimizerResult.annual401kIncrease < 0 ? 'Less to retirement' : 'No change'],
      ]

      autoTable(doc, {
        head: [['Metric', 'Value', 'Direction']],
        body: optimizerRows,
        startY: y,
        ...tableStyles,
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 55, halign: 'right' }, 2: { cellWidth: 'auto', textColor: [100, 100, 100], fontStyle: 'italic' } },
      })
      y = doc.lastAutoTable.finalY + 4

      // Summary sentence mirroring the UI
      y = ensureSpace(y, 16)
      doc.setFontSize(8)
      doc.setTextColor(60)
      if (optimizerResult.newPercent > optimizerResult.currentPercent) {
        doc.text(
          `Increasing 401(k) from ${currentPctLabel} to ${newPctLabel}: lose ${formatCurrency(Math.abs(optimizerResult.takeHomeDiff))}/paycheck in take-home, ` +
          `save ${formatCurrency(optimizerResult.annualTaxSavings)}/yr in taxes, add ${formatCurrency(optimizerResult.annual401kIncrease)}/yr to retirement.`,
          margin, y, { maxWidth: maxContentWidth }
        )
      } else if (optimizerResult.newPercent < optimizerResult.currentPercent) {
        doc.text(
          `Decreasing 401(k) from ${currentPctLabel} to ${newPctLabel}: gain ${formatCurrency(Math.abs(optimizerResult.takeHomeDiff))}/paycheck in take-home, ` +
          `pay ${formatCurrency(Math.abs(optimizerResult.annualTaxSavings))}/yr more in taxes.`,
          margin, y, { maxWidth: maxContentWidth }
        )
      } else {
        doc.text('The slider is at your current contribution rate. No changes to compare.', margin, y, { maxWidth: maxContentWidth })
      }
      doc.setTextColor(0)
      y += 14
    }

    // =========================================================================
    // 10. HSA TAX SAVINGS CALLOUT
    // =========================================================================
    if (hsaSavingsSelf > 0 || hsaSavingsFamily > 0) {
      y = addSectionHeader('HSA Tax Savings Opportunity', y)

      const hsaRows = []
      hsaRows.push(['Current Annual HSA', formatCurrency(currentAnnualHSA)])
      if (hsaSavingsSelf > 0) {
        hsaRows.push(['Individual Max (' + formatCurrency(hsaMaxSelf) + '/yr)', `Save ~${formatCurrency(hsaSavingsSelf)}/yr in taxes`])
      }
      if (hsaSavingsFamily > 0 && currentAnnualHSA < hsaMaxFamily) {
        hsaRows.push(['Family Max (' + formatCurrency(hsaMaxFamily) + '/yr)', `Save ~${formatCurrency(hsaSavingsFamily)}/yr in taxes`])
      }

      autoTable(doc, {
        head: [['HSA Scenario', 'Tax Impact']],
        body: hsaRows,
        startY: y,
        ...tableStyles,
        columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 70, halign: 'right' } },
      })
      y = doc.lastAutoTable.finalY + 4

      // Descriptive text
      y = ensureSpace(y, 12)
      doc.setFontSize(8)
      doc.setTextColor(60)
      const hsaDesc = currentAnnualHSA > 0
        ? `You are contributing ${formatCurrency(currentAnnualHSA)}/yr to your HSA. Maximizing your contributions could yield additional tax savings at your ${benchmark.marginalRate}% marginal rate.`
        : `You are not currently contributing to an HSA. Starting HSA contributions could save you significantly at your ${benchmark.marginalRate}% marginal rate.`
      doc.text(hsaDesc, margin, y, { maxWidth: maxContentWidth })
      doc.setTextColor(0)
      y += 12
    }

    // =========================================================================
    // FOOTER DISCLAIMER
    // =========================================================================
    y = ensureSpace(y, 20)
    doc.setDrawColor(200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 5
    doc.setFontSize(7)
    doc.setTextColor(140)
    doc.text(
      'Disclaimer: This is a simplified estimate for educational purposes only and should not be considered tax advice. ' +
      'Actual tax liability depends on many additional factors. Consult a qualified tax professional for personalized guidance. ' +
      'Tax brackets shown are for the 2025 tax year.',
      margin, y, { maxWidth: maxContentWidth }
    )

    doc.save(`paycheck-breakdown-${Date.now()}.pdf`)
    trackExportReport()
    } catch (err) {
      console.error('PDF export failed:', err)
    }
  }

  return (
    <div className="space-y-4">
      {/* Pay Details */}
      <GlassCard>
        <CollapseHeader label="Pay Details" open={showPayDetails} onToggle={() => setShowPayDetails(!showPayDetails)} />
        {showPayDetails && (
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <InputField label="Pay Frequency">
                <select
                  value={payFrequency}
                  onChange={(e) => setPayFrequency(e.target.value)}
                  className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1 appearance-none"
                >
                  {Object.entries(PAY_FREQUENCIES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </InputField>

              <InputField label="Gross Pay Per Period">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
                  <input
                    type="number"
                    value={grossPay}
                    onChange={(e) => setGrossPay(parseFloat(e.target.value) || 0)}
                    className="w-full bg-input border border-input rounded-xl py-2.5 pl-7 pr-3 text-sm text-heading focus:outline-none focus:ring-1"
                    min={0}
                  />
                </div>
              </InputField>

              <InputField label="Filing Status">
                <div className="flex gap-2">
                  {FILING_STATUSES.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setFilingStatus(key)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${
                        filingStatus === key
                          ? 'bg-primary text-on-primary'
                          : 'bg-subtle text-muted hover:text-heading'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </InputField>

              <InputField label="State">
                <select
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1 appearance-none"
                >
                  {STATE_TAX_DATA.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name}{s.rate !== null ? ` (${s.rate}%)` : ''}
                    </option>
                  ))}
                </select>
              </InputField>
            </div>

            {stateCode === 'CUSTOM' && (
              <InputField label="Custom State Tax Rate">
                <div className="relative">
                  <input
                    type="number"
                    value={customStateTaxRate}
                    onChange={(e) => setCustomStateTaxRate(parseFloat(e.target.value) || 0)}
                    className="w-full bg-input border border-input rounded-xl py-2.5 px-3 pr-8 text-sm text-heading focus:outline-none focus:ring-1"
                    step={0.1}
                    min={0}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">%</span>
                </div>
              </InputField>
            )}

            {/* Pay Stub Actuals toggle */}
            <div className="pt-2 border-t border-subtle">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showStubActuals}
                  onChange={(e) => setShowStubActuals(e.target.checked)}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-xs font-medium text-body">Use pay stub actuals</span>
                <span className="text-[10px] text-muted">— compare real withholding against estimated liability</span>
              </label>
              {showStubActuals && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                  <NumberInput label="Federal Tax Withheld" value={actualFederalWithheld} onChange={setActualFederalWithheld} prefix="$" suffix="/period" />
                  <NumberInput label="State Tax Withheld" value={actualStateWithheld} onChange={setActualStateWithheld} prefix="$" suffix="/period" />
                  <NumberInput label="Social Security Withheld" value={actualSSWithheld} onChange={setActualSSWithheld} prefix="$" suffix="/period" />
                  <NumberInput label="Medicare Withheld" value={actualMedicareWithheld} onChange={setActualMedicareWithheld} prefix="$" suffix="/period" />
                </div>
              )}
            </div>
          </div>
        )}
      </GlassCard>

      {/* Pre-Tax Deductions */}
      <GlassCard>
        <CollapseHeader label="Pre-Tax Deductions" open={showPreTax} onToggle={() => setShowPreTax(!showPreTax)} />
        {showPreTax && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <InputField label="Traditional 401(k)">
              <div className="flex gap-2">
                <button
                  onClick={() => setUse401kPercent(true)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition ${use401kPercent ? 'bg-primary text-on-primary' : 'bg-subtle text-muted'}`}
                >%</button>
                <button
                  onClick={() => setUse401kPercent(false)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition ${!use401kPercent ? 'bg-primary text-on-primary' : 'bg-subtle text-muted'}`}
                >$</button>
                <div className="relative flex-1">
                  <input
                    type="number"
                    value={use401kPercent ? traditional401kPercent : traditional401kDollar}
                    onChange={(e) => use401kPercent
                      ? setTraditional401kPercent(parseFloat(e.target.value) || 0)
                      : setTraditional401kDollar(parseFloat(e.target.value) || 0)
                    }
                    className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1"
                    min={0}
                    step={use401kPercent ? 1 : 10}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">
                    {use401kPercent ? '%' : '/period'}
                  </span>
                </div>
              </div>
            </InputField>

            <NumberInput label="HSA" value={hsaPerPeriod} onChange={setHsaPerPeriod} prefix="$" suffix="/period" />
            <NumberInput label="Health Insurance" value={healthInsurance} onChange={setHealthInsurance} prefix="$" suffix="/period" />
          </div>
        )}
      </GlassCard>

      {/* Post-Tax Deductions */}
      <GlassCard>
        <CollapseHeader label="Post-Tax Deductions" open={showPostTax} onToggle={() => setShowPostTax(!showPostTax)} />
        {showPostTax && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            <InputField label="Roth 401(k)">
              <div className="flex gap-2">
                <button
                  onClick={() => setUseRoth401kPercent(true)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition ${useRoth401kPercent ? 'bg-primary text-on-primary' : 'bg-subtle text-muted'}`}
                >%</button>
                <button
                  onClick={() => setUseRoth401kPercent(false)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition ${!useRoth401kPercent ? 'bg-primary text-on-primary' : 'bg-subtle text-muted'}`}
                >$</button>
                <div className="relative flex-1">
                  <input
                    type="number"
                    value={useRoth401kPercent ? roth401kPercent : roth401kDollar}
                    onChange={(e) => useRoth401kPercent
                      ? setRoth401kPercent(parseFloat(e.target.value) || 0)
                      : setRoth401kDollar(parseFloat(e.target.value) || 0)
                    }
                    className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:ring-1"
                    min={0}
                    step={useRoth401kPercent ? 1 : 10}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">
                    {useRoth401kPercent ? '%' : '/period'}
                  </span>
                </div>
              </div>
            </InputField>

            <NumberInput label="Other Post-Tax" value={otherPostTax} onChange={setOtherPostTax} prefix="$" suffix="/period" />
            <NumberInput label="401(k) Loan Repayment" value={loanRepayment401k} onChange={setLoanRepayment401k} prefix="$" suffix="/period" />
            <NumberInput label="Union Dues" value={unionDues} onChange={setUnionDues} prefix="$" suffix="/period" />
            <NumberInput label="Life/Disability Insurance" value={lifeDisabilityInsurance} onChange={setLifeDisabilityInsurance} prefix="$" suffix="/period" />
            <NumberInput label="Other Miscellaneous" value={otherMiscPostTax} onChange={setOtherMiscPostTax} prefix="$" suffix="/period" />
          </div>
        )}
      </GlassCard>

      {/* W-4 Adjustments */}
      <GlassCard>
        <CollapseHeader label="W-4 Adjustments" open={showW4} onToggle={() => setShowW4(!showW4)} />
        {showW4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            <NumberInput label="Extra Withholding" value={extraWithholding} onChange={setExtraWithholding} prefix="$" suffix="/period" />
            <NumberInput label="Dependents Credit (Annual)" value={dependentsCredit} onChange={setDependentsCredit} prefix="$" suffix="/year" />
            <NumberInput label="Other Income (Annual)" value={otherIncome} onChange={setOtherIncome} prefix="$" suffix="/year" />
            <NumberInput label="Deductions Above Standard" value={deductionsAboveStandard} onChange={setDeductionsAboveStandard} prefix="$" suffix="/year" />
          </div>
        )}
      </GlassCard>

      {/* Results */}
      <div className="space-y-4">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Gross Pay" value={formatCurrency(result.grossPay)} icon="$" color="primary" subValue={freq.label} />
          <StatCard label="Total Taxes" value={formatCurrency(result.totalTax)} icon="%" color="negative" subValue={`${result.effectiveRate}% effective`} />
          <StatCard label="Take-Home" value={formatCurrency(result.netPay)} icon="$" color="positive" subValue={`${formatCurrency(result.annualNet)}/yr`} />
          <StatCard label="Effective Rate" value={`${result.effectiveRate}%`} icon="%" color={result.effectiveRate > 30 ? 'negative' : result.effectiveRate > 20 ? 'warning' : 'primary'} subValue="Tax only" />
        </div>

        {/* Waterfall Breakdown */}
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Pay Breakdown</h3>
          <div className="space-y-1">
            {waterfall.map((row, i) => (
              <div
                key={i}
                className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${
                  row.type === 'gross' ? 'bg-primary-soft font-semibold' :
                  row.type === 'net' ? 'bg-positive-soft font-semibold' :
                  ''
                }`}
              >
                <span className="text-heading">{row.label}</span>
                <div className="flex items-center gap-4">
                  {row.type !== 'gross' && row.type !== 'net' && (
                    <span className={`text-xs ${row.type === 'tax' ? 'text-negative' : 'text-muted'}`}>
                      {formatCurrency(Math.abs(row.amount))}
                    </span>
                  )}
                  <span className={`font-medium min-w-[80px] text-right ${
                    row.type === 'net' ? 'text-positive' : 'text-heading'
                  }`}>
                    {formatCurrency(row.type === 'gross' || row.type === 'net' ? row.amount : row.running)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Per-Period vs Annual */}
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Per-Period vs Annual</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-subtle">
                  <th className="text-left py-2 text-muted font-medium">Category</th>
                  <th className="text-right py-2 text-muted font-medium">{freq.label}</th>
                  <th className="text-right py-2 text-muted font-medium">Annual</th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow label="Gross Pay" perPeriod={result.grossPay} annual={result.annualGross} />
                {result.totalPreTax > 0 && <ComparisonRow label="Pre-Tax Deductions" perPeriod={result.totalPreTax} annual={result.totalPreTax * freq.periods} />}
                <ComparisonRow label="Federal Tax" perPeriod={result.federalTax} annual={result.annualFederalTax} />
                {result.stateTax > 0 && <ComparisonRow label="State Tax" perPeriod={result.stateTax} annual={result.annualStateTax} />}
                <ComparisonRow label="Social Security" perPeriod={result.socialSecurity} annual={result.annualSS} />
                <ComparisonRow label="Medicare" perPeriod={result.medicare} annual={result.annualMedicare} />
                {result.roth401k > 0 && <ComparisonRow label="Roth 401(k)" perPeriod={result.roth401k} annual={result.roth401k * freq.periods} />}
                {result.otherPostTax > 0 && <ComparisonRow label="Other Post-Tax" perPeriod={result.otherPostTax} annual={result.otherPostTax * freq.periods} />}
                {result.loanRepayment401k > 0 && <ComparisonRow label="401(k) Loan" perPeriod={result.loanRepayment401k} annual={result.loanRepayment401k * freq.periods} />}
                {result.unionDues > 0 && <ComparisonRow label="Union Dues" perPeriod={result.unionDues} annual={result.unionDues * freq.periods} />}
                {result.lifeDisabilityInsurance > 0 && <ComparisonRow label="Life/Disability Ins." perPeriod={result.lifeDisabilityInsurance} annual={result.lifeDisabilityInsurance * freq.periods} />}
                {result.otherMiscPostTax > 0 && <ComparisonRow label="Other Misc." perPeriod={result.otherMiscPostTax} annual={result.otherMiscPostTax * freq.periods} />}
                <tr className="border-t border-subtle font-semibold">
                  <td className="py-2 text-heading">Net Pay</td>
                  <td className="py-2 text-right text-positive">{formatCurrency(result.netPay)}</td>
                  <td className="py-2 text-right text-positive">{formatCurrency(result.annualNet)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </GlassCard>

        {/* Pie Chart */}
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Pay Distribution</h3>
          <div ref={chartRef} className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ payload }) => {
                    if (!payload?.[0]) return null
                    const { name, value } = payload[0].payload
                    return (
                      <div className="glass px-3 py-2 text-xs rounded-lg">
                        <p className="text-heading font-medium">{name}</p>
                        <p className="text-body">{formatCurrency(value)}</p>
                      </div>
                    )
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 mt-4 justify-center">
            {pieData.map((entry, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-muted">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                {entry.name}
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Tax Benchmark */}
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Tax Benchmark</h3>
          <p className="text-xs text-muted mb-4">
            Based on {formatCurrency(benchmark.annualGross)}/yr gross income, {filingStatus === 'mfj' ? 'Married Filing Jointly' : filingStatus === 'hoh' ? 'Head of Household' : 'Single'} filing status
          </p>

          {/* Rate Summary */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="text-center p-3 rounded-xl bg-subtle">
              <div className="text-lg font-bold text-heading">{benchmark.marginalRate}%</div>
              <div className="text-xs text-muted">Marginal Rate</div>
              <div className="text-[10px] text-faint mt-0.5">Tax bracket</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-subtle">
              <div className="text-lg font-bold text-heading">{benchmark.federalEffective}%</div>
              <div className="text-xs text-muted">Federal Effective</div>
              <div className="text-[10px] text-faint mt-0.5">What you actually pay</div>
            </div>
            <div className="text-center p-3 rounded-xl bg-subtle">
              <div className="text-lg font-bold text-heading">{benchmark.totalEffective}%</div>
              <div className="text-xs text-muted">Total Effective</div>
              <div className="text-[10px] text-faint mt-0.5">All taxes combined</div>
            </div>
          </div>

          {/* Bracket Breakdown */}
          {benchmark.brackets.length > 0 && (
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-muted mb-2 uppercase tracking-wide">Federal Bracket Breakdown</h4>
              <div className="space-y-2">
                {benchmark.brackets.map((b, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-body font-medium">{b.rate}% bracket</span>
                      <span className="text-muted">
                        {formatCurrency(b.taxableAmount)} taxed = {formatCurrency(b.tax)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-subtle overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${b.fillPercent}%`,
                          backgroundColor: `color-mix(in srgb, var(--primary) ${40 + b.rate * 1.5}%, var(--secondary))`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Withholding vs Estimated Comparison */}
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-muted mb-2 uppercase tracking-wide">
              Current Withholding vs Estimated Liability
              {benchmark.hasActuals && <span className="ml-2 text-primary font-normal">(using pay stub data)</span>}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-subtle">
                    <th className="text-left py-2 text-muted font-medium">Category</th>
                    <th className="text-right py-2 text-muted font-medium">Withholding/yr</th>
                    <th className="text-right py-2 text-muted font-medium">Estimated/yr</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-subtle">
                    <td className="py-2 text-body">Federal Income Tax</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(benchmark.withholdingFederal)}</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(result.annualFederalTax)}</td>
                  </tr>
                  <tr className="border-b border-subtle">
                    <td className="py-2 text-body">State Income Tax</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(benchmark.withholdingState)}</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(result.annualStateTax)}</td>
                  </tr>
                  <tr className="border-b border-subtle">
                    <td className="py-2 text-body">Social Security</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(benchmark.withholdingSS)}</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(result.annualSS)}</td>
                  </tr>
                  <tr className="border-b border-subtle">
                    <td className="py-2 text-body">Medicare</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(benchmark.withholdingMedicare)}</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(result.annualMedicare)}</td>
                  </tr>
                  <tr className="font-semibold">
                    <td className="py-2 text-heading">Total</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(benchmark.currentAnnualWithholding)}</td>
                    <td className="py-2 text-right text-heading">{formatCurrency(benchmark.totalAnnualTax)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Withholding status indicator */}
            <div className={`mt-3 p-3 rounded-xl flex items-center gap-2 ${
              Math.abs(benchmark.withholdingDiff) < benchmark.annualGross * 0.01
                ? 'bg-positive/10 border border-positive/20'
                : benchmark.withholdingDiff > 0
                  ? 'bg-primary-soft border border-primary/20'
                  : 'bg-warning/10 border border-warning/20'
            }`}>
              <span className="text-sm">
                {Math.abs(benchmark.withholdingDiff) < benchmark.annualGross * 0.01 ? (
                  <span className="text-positive font-medium">On track</span>
                ) : benchmark.withholdingDiff > 0 ? (
                  <span className="text-primary font-medium">Over-withheld by ~{formatCurrency(Math.abs(benchmark.withholdingDiff))}</span>
                ) : (
                  <span className="text-warning font-medium">Under-withheld by ~{formatCurrency(Math.abs(benchmark.withholdingDiff))}</span>
                )}
              </span>
              <span className="text-xs text-muted">
                {Math.abs(benchmark.withholdingDiff) < benchmark.annualGross * 0.01
                  ? '— Your withholding closely matches your estimated tax liability.'
                  : benchmark.withholdingDiff > 0
                    ? '— You may get a refund. Consider adjusting W-4 to keep more per paycheck.'
                    : '— You may owe at tax time. Consider increasing withholding on your W-4.'}
              </span>
            </div>
          </div>

          {/* Context note */}
          <div className="p-3 rounded-xl bg-primary-soft mb-4">
            <p className="text-xs text-body leading-relaxed">
              <strong className="text-heading">What does this mean?</strong> Your marginal rate ({benchmark.marginalRate}%) is the rate on your <em>last dollar</em> of income.
              Your effective rate ({benchmark.federalEffective}%) is what you actually pay overall in federal taxes. Most of your income is taxed at lower rates —
              that's how progressive tax brackets work. {benchmark.ficaRate > 0 && `FICA taxes (Social Security + Medicare) add another ${benchmark.ficaRate}% on top.`}
            </p>
          </div>

          {/* Disclaimer */}
          <div className="p-3 rounded-xl border border-warning/30 bg-warning/5">
            <div className="flex gap-2">
              <svg className="w-4 h-4 text-warning shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-xs text-muted leading-relaxed">
                <strong className="text-body">Disclaimer:</strong> This is a simplified estimate for educational purposes only and should not be considered tax advice.
                Actual tax liability depends on many additional factors including itemized deductions, tax credits (child tax credit, education credits, etc.),
                capital gains, self-employment income, AMT, multiple income sources, local taxes, and more.
                Consult a qualified tax professional for personalized guidance.
                Tax brackets shown are for the 2025 tax year.
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Optimizer Section */}
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Optimizer</h3>

          {/* 401k What-If Slider */}
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-muted mb-3 uppercase tracking-wide">What If: 401(k) Contribution</h4>
            <div className="flex items-center gap-4 mb-3">
              <span className="text-xs text-muted whitespace-nowrap">0%</span>
              <input
                type="range"
                min={0}
                max={30}
                step={1}
                value={optimizer401k !== null ? optimizer401k : (use401kPercent ? traditional401kPercent : 6)}
                onChange={e => setOptimizer401k(parseInt(e.target.value))}
                className="flex-1 accent-primary h-2"
              />
              <span className="text-xs text-muted whitespace-nowrap">30%</span>
              <span className="text-sm font-bold text-heading min-w-[40px] text-right">
                {optimizer401k !== null ? optimizer401k : (use401kPercent ? traditional401kPercent : '—')}%
              </span>
            </div>

            {optimizerResult && (
              <div className={`p-3 rounded-xl ${optimizerResult.takeHomeDiff < 0 ? 'bg-primary-soft' : 'bg-positive/10'}`}>
                <p className="text-sm text-heading leading-relaxed">
                  {optimizerResult.newPercent > optimizerResult.currentPercent ? (
                    <>
                      If you increase 401(k) from <strong>{Math.round(optimizerResult.currentPercent)}%</strong> to <strong>{optimizerResult.newPercent}%</strong>,
                      you lose <strong className="text-negative">{formatCurrency(Math.abs(optimizerResult.takeHomeDiff))}/paycheck</strong> in take-home
                      but save <strong className="text-positive">{formatCurrency(optimizerResult.annualTaxSavings)}/yr</strong> in taxes
                      and add <strong className="text-primary">{formatCurrency(optimizerResult.annual401kIncrease)}/yr</strong> to retirement savings.
                    </>
                  ) : optimizerResult.newPercent < optimizerResult.currentPercent ? (
                    <>
                      If you decrease 401(k) from <strong>{Math.round(optimizerResult.currentPercent)}%</strong> to <strong>{optimizerResult.newPercent}%</strong>,
                      you gain <strong className="text-positive">{formatCurrency(Math.abs(optimizerResult.takeHomeDiff))}/paycheck</strong> in take-home
                      but pay <strong className="text-negative">{formatCurrency(Math.abs(optimizerResult.annualTaxSavings))}/yr</strong> more in taxes.
                    </>
                  ) : (
                    <>This is your current 401(k) contribution rate. Move the slider to see the impact of changes.</>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* HSA Callout */}
          {(hsaSavingsSelf > 0 || hsaSavingsFamily > 0) && (
            <div className="p-3 rounded-xl bg-primary-soft">
              <h4 className="text-xs font-semibold text-muted mb-2 uppercase tracking-wide">HSA Tax Savings Opportunity</h4>
              <p className="text-sm text-heading leading-relaxed">
                {currentAnnualHSA > 0 ? (
                  <>You're contributing {formatCurrency(currentAnnualHSA)}/yr to your HSA. </>
                ) : (
                  <>You're not contributing to an HSA. </>
                )}
                {hsaSavingsSelf > 0 && (
                  <>Contributing the max individual amount (<strong>{formatCurrency(hsaMaxSelf)}/yr</strong>) would save you ~<strong className="text-positive">{formatCurrency(hsaSavingsSelf)}/yr</strong> in taxes. </>
                )}
                {hsaSavingsFamily > 0 && currentAnnualHSA < hsaMaxFamily && (
                  <>The family max (<strong>{formatCurrency(hsaMaxFamily)}/yr</strong>) saves ~<strong className="text-positive">{formatCurrency(hsaSavingsFamily)}/yr</strong>.</>
                )}
              </p>
            </div>
          )}
        </GlassCard>

        {/* PDF Export */}
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
      </div>
    </div>
  )
}

function InputField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      {children}
    </div>
  )
}

function NumberInput({ label, value, onChange, prefix, suffix }) {
  return (
    <InputField label={label}>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix && suffix ? 'pl-7 pr-16' : prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
          }`}
          min={0}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
      </div>
    </InputField>
  )
}

function CollapseHeader({ label, open, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between text-sm font-semibold text-body"
    >
      <span>{label}</span>
      <svg className={`w-4 h-4 text-muted transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  )
}

function ComparisonRow({ label, perPeriod, annual }) {
  return (
    <tr className="border-b border-subtle">
      <td className="py-2 text-body">{label}</td>
      <td className="py-2 text-right text-heading">{formatCurrency(perPeriod)}</td>
      <td className="py-2 text-right text-heading">{formatCurrency(annual)}</td>
    </tr>
  )
}
